<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h3>Brake Metals Finish</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-row no-gutters>
                <v-col cols="12" md="6">
                    <v-combobox
                        v-model="item.material"
                        :items="materialOptions"
                        item-text="descripcion"
                        item-value="descripcion"
                        label="Material *"
                        prepend-icon="mdi-rectangle-outline"
                        hide-details="auto"
                        dense
                        clearable
                        :rules="[rules.requiredStr]"
                        @change="onMaterialChange"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="item.thicknessMm"
                        label="Thickness (mm) *"
                        type="number"
                        step="0.1"
                        min="0"
                        prepend-icon="mdi-ruler"
                        hide-details="auto"
                        dense
                        :rules="[rules.requiredThickness]"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="mt-2">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="item.thicknessIn"
                        label="Thickness (in) *"
                        type="number"
                        prepend-icon="mdi-ruler"
                        hide-details="auto"
                        dense
                        :rules="[rules.requiredThickness]"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-select
                        v-model="item.finishClass"
                        :items="finishClassOptions"
                        label="Finish class *"
                        prepend-icon="mdi-format-list-bulleted"
                        hide-details="auto"
                        dense
                        :rules="[rules.requiredStr]"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="mt-2">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="item.paintCost1Sides"
                        hide-details="auto"
                        prepend-icon="mdi-currency-usd"
                        label="PV one side USD/ft² *"
                        type="number"
                        step="0.01"
                        dense
                        :rules="[rules.requiredNumber]"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="item.paintCost2Sides"
                        hide-details="auto"
                        prepend-icon="mdi-currency-usd"
                        label="PV two sides USD/ft² *"
                        type="number"
                        step="0.01"
                        dense
                        :rules="[rules.requiredNumber]"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="saveProcess"
                rounded
                :loading="loading"
            >
                SAVE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import { objDiff } from '@/helpers/objDiff.js'
import API from '@/services/api'
import {
    brakeMetalsFinishRowKey,
    brakeMetalsFinishDisplayName,
} from '@/constants/brakeMetalsFinishSettings.js'
//import BrakeMetalsApi from '@/services/brake_metals_api'

export default {
    name: 'FinishFormBrakeMetal',
    props: {
        item: {
            type: Object,
            default: () => ({}),
        },
        originalItem: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            default: false,
        },
        setting: {
            type: Object,
            required: true,
        },
        /** Claves `brakeMetalsFinishRowKey` ya usadas (excluir fila en edición vía `excludeFinishId` en el padre). */
        duplicateRowKeys: {
            type: Array,
            default: () => [],
        },
        finishClassOptions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            materialOptions: [],
            rules: {
                requiredNumber: v => v === 0 || !!v || 'Required value',
                requiredStr: v =>
                    v !== null && v !== undefined && String(v).trim() !== ''
                        ? true
                        : 'Required',
                requiredThickness: v => {
                    const n = Number(v)
                    return Number.isFinite(n) && n > 0
                        ? true
                        : 'Valid thickness required'
                },
            },
        }
    },
    computed: {
        itemDiff() {
            if (!this.createForm) {
                return objDiff(this.originalItem, this.item)
            }
            return null
        },
    },
    watch: {
        item: {
            deep: true,
            handler() {
                this.syncDisplayName()
            },
        },
    },
    async mounted() {
        try {
            this.loading = true
            //const materials = await BrakeMetalsApi.getMaterials()
            this.materialOptions = ['GALVANIZED SHEET', 'ALUMINUM SHEET']
            this.syncDisplayName()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        normalizeMaterial(material) {
            if (typeof material === 'string') {
                return material.trim()
            }
            if (material && typeof material === 'object') {
                return String(
                    material.descripcion || material.description || ''
                ).trim()
            }
            return ''
        },
        setThicknessMm() {
            const normalizedMaterial = this.normalizeMaterial(
                this.item.material
            )
            this.$set(this.item, 'material', normalizedMaterial)
            const thicknessMm = this.materialOptions.find(
                m => m.descripcion === normalizedMaterial
            )?.espesor
            if (!Number.isFinite(Number(thicknessMm))) {
                return
            }
            const thicknessIn = thicknessMm / 25.4
            this.$set(this.item, 'thicknessMm', thicknessMm)
            this.$set(this.item, 'thicknessIn', thicknessIn)
        },
        onMaterialChange() {
            const normalizedMaterial = this.normalizeMaterial(
                this.item.material
            )
            this.$set(this.item, 'material', normalizedMaterial)
            this.setThicknessMm()
        },
        syncDisplayName() {
            const row = this.item || {}
            this.$set(
                this.item,
                'name',
                brakeMetalsFinishDisplayName({
                    material: row.material,
                    thicknessMm: row.thicknessMm,
                    finishClass: row.finishClass,
                })
            )
        },
        rowKey() {
            return brakeMetalsFinishRowKey(this.item || {})
        },
        duplicateError() {
            const row = this.item || {}
            const material = this.normalizeMaterial(row.material)
            const tmm = Number(row.thicknessMm)
            const tin = Number(row.thicknessIn)
            if (
                !material ||
                !row.finishClass ||
                !Number.isFinite(tmm) ||
                !Number.isFinite(tin) ||
                tmm <= 0 ||
                tin <= 0
            ) {
                return null
            }
            const key = this.rowKey()
            if (this.duplicateRowKeys.includes(key)) {
                return 'Already exists a row with the same material, thickness and finish class'
            }
            return null
        },
        async saveProcess() {
            this.$set(
                this.item,
                'material',
                this.normalizeMaterial(this.item.material)
            )
            this.syncDisplayName()
            const dup = this.duplicateError()
            if (dup) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: dup,
                })
                return
            }
            if (this.createForm) {
                await this.create()
            } else {
                await this.update()
            }
        },
        async create() {
            try {
                const material = this.normalizeMaterial(this.item.material)
                this.$set(this.item, 'material', material)
                const tmm = Number(this.item.thicknessMm)
                const tin = Number(this.item.thicknessIn)
                if (
                    !material ||
                    !this.item.finishClass ||
                    !Number.isFinite(tmm) ||
                    !Number.isFinite(tin) ||
                    tmm <= 0 ||
                    tin <= 0
                ) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message:
                            'Complete material, thickness and finish class',
                    })
                    return
                }
                this.loading = true
                const created = await API.createFinishBrakeMetals({
                    settingId: this.setting.id,
                    material,
                    thicknessMm: tmm,
                    thicknessIn: tin,
                    finishClass: this.item.finishClass,
                    paintCost1Sides: Number(this.item.paintCost1Sides) || 0,
                    paintCost2Sides: Number(this.item.paintCost2Sides) || 0,
                })
                this.$emit('addItem', created)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async update() {
            try {
                this.loading = true
                this.syncDisplayName()
                const patch = this.itemDiff || {}
                if (Object.keys(patch).length === 0) {
                    this.$emit('close')
                    return
                }
                delete patch.name
                const body = { ...patch }
                if (body.material != null) {
                    body.material = this.normalizeMaterial(body.material)
                }
                const updated = await API.updateFinishBrakeMetals({
                    settingId: this.setting.id,
                    id: this.item.id,
                    ...body,
                })
                this.$emit('replaceItem', updated)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>
